import React, { useState, useEffect } from 'react';
import { FileText, Save, RefreshCw, AlertCircle, Info } from 'lucide-react';
import axios from 'axios';

const DocumentSettings = () => {
  const [currentNumber, setCurrentNumber] = useState('');
  const [newStartNumber, setNewStartNumber] = useState('');
  const [nextNumber, setNextNumber] = useState('');
  const [lastUpdateInfo, setLastUpdateInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadDocNumberSettings();
  }, []);

  const loadDocNumberSettings = async () => {
    try {
      const response = await axios.get('/admin/settings/doc-number');
      setCurrentNumber(response.data.data.currentNumber);
      setLastUpdateInfo(response.data.data);

      // Get next number preview
      const nextResponse = await axios.get('/admin/settings/doc-number/next');
      setNextNumber(nextResponse.data.data.nextNumber);
    } catch (error) {
      console.error('Error loading settings:', error);
      showMessage('خطأ في تحميل الإعدادات', false);
    }
  };

  const showMessage = (text, success = true) => {
    setMessage({ text, success });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newStartNumber) {
      showMessage('الرجاء إدخال رقم البداية الجديد', false);
      return;
    }

    if (!/^\d{8}$/.test(newStartNumber)) {
      showMessage('رقم المستند يجب أن يكون 8 أرقام', false);
      return;
    }

    setLoading(true);
    try {
      await axios.post('/admin/settings/doc-number', {
        startNumber: newStartNumber
      });
      
      showMessage('تم تحديث رقم البداية بنجاح');
      setNewStartNumber('');
      loadDocNumberSettings();
    } catch (error) {
      if (error.response?.data?.message) {
        showMessage(error.response.data.message, false);
      } else {
        showMessage('خطأ في تحديث رقم البداية', false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">إعدادات الاستمارات</h1>
        <p className="mt-1 text-sm text-gray-600">
          إدارة أرقام الاستمارات والتحكم في تسلسل الأرقام
        </p>
      </div>

      {/* Current Status Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-medium text-gray-900">معلومات أرقام الاستمارات</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <dt className="text-sm font-medium text-gray-500">الرقم التالي للاستمارة</dt>
              <dd className="mt-2 text-2xl font-bold text-gray-900">{nextNumber || '00000000'}</dd>
              <p className="mt-1 text-xs text-gray-500">هذا الرقم سيحصل عليه الاستمارة التالية</p>
            </div>

            {lastUpdateInfo?.lastUpdated && (
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-sm font-medium text-gray-500">آخر تحديث</dt>
                <dd className="mt-2 text-sm text-gray-900">
                  {new Date(lastUpdateInfo.lastUpdated).toLocaleDateString('ar-EG', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </dd>
                {lastUpdateInfo.lastUpdatedBy && (
                  <p className="mt-1 text-xs text-gray-500">
                    بواسطة: {lastUpdateInfo.lastUpdatedBy.fullName}
                  </p>
                )}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <dt className="text-sm font-medium text-gray-500">نمط الترقيم</dt>
              <dd className="mt-2 text-sm text-gray-900">
                <span className="font-mono bg-white px-2 py-1 rounded">YYYY-XXXXXXXX</span>
              </dd>
              <p className="mt-1 text-xs text-gray-500">السنة متبوعة برقم مكون من 8 أرقام</p>
            </div>
          </div>
        </div>
      </div>

      {/* Update Start Number Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">تغيير رقم البداية</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="newStartNumber" className="block text-sm font-medium text-gray-700">
                رقم البداية الجديد
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="newStartNumber"
                  value={newStartNumber}
                  onChange={(e) => setNewStartNumber(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                  placeholder="12345678"
                  maxLength="8"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                أدخل رقم مكون من 8 أرقام. الاستمارات الجديدة ستحصل على أرقام تبدأ من هذا الرقم.
              </p>
            </div>

            {/* Warning Alert */}
            <div className="rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="mr-3">
                  <h3 className="text-sm font-medium text-yellow-800">تنبيه مهم</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc pr-5 space-y-1">
                      <li>لا يمكن استخدام رقم موجود بالفعل أو أقل من رقم مستخدم حالياً</li>
                      <li>هذا التغيير سيؤثر على جميع الاستمارات الجديدة من الآن فصاعداً</li>
                      <li>تأكد من اختيار رقم مناسب لتجنب التداخل مع الأرقام الحالية</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Alert */}
            {message && (
              <div className={`rounded-md p-3 ${
                message.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.success ? (
                      <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                  <div className="mr-3">
                    <p className={`text-sm ${message.success ? 'text-green-700' : 'text-red-700'}`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 space-x-reverse">
              <button
                type="button"
                onClick={loadDocNumberSettings}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className="w-4 h-4 ml-2" />
                تحديث
              </button>
              
              <button
                type="submit"
                disabled={loading || !newStartNumber}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                    جاري الحفظ...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="w-4 h-4 ml-2" />
                    حفظ التغييرات
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info className="h-5 w-5 text-blue-400" />
          </div>
          <div className="mr-3">
            <h3 className="text-sm font-medium text-blue-800">كيفية عمل نظام الترقيم</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                عند إنشاء استمارة جديدة، تحصل تلقائياً على رقم فريد مكون من السنة الحالية متبوعة برقم تسلسلي من 8 أرقام.
                مثال: 2025-12345678
              </p>
              <p className="mt-2">
                يمكنك تغيير نقطة البداية للأرقام التسلسلية في أي وقت، وستبدأ الاستمارات الجديدة في الحصول على أرقام من النقطة الجديدة.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSettings;